import React, { FC } from 'react';
import { captureExceptionEvent } from '@blocs.observability';
import styled from 'styled-components';
import { FormikProps, Formik, Form } from 'formik';
import { useGlobalNotification } from '@minecraft.notification-provider';
import Modal from '@minecraft.modal';
import { useTranslation } from '@blocs.i18n';
import { FilterValues } from '../shared/types';
import { ClearButton } from '../shared/styles';

const StyledNote = styled.div`
  margin-bottom: 1rem;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledClearButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-bottom: 1rem;
`;

const StyledClearButton = styled(ClearButton)`
  font-size: 1rem;
  margin-right: 0;
`;

interface Props {
  loading: boolean;
  initialFilters: FilterValues;
  accountAppliedFilters: FilterValues;
  toggleClick: () => void;
  onFiltersChange: (newFilters: FilterValues) => void;
  handleSubmit: (values: FilterValues) => Promise<boolean>;
}

const FilterSettingsModal: FC<Props> = ({
  loading,
  initialFilters,
  accountAppliedFilters,
  toggleClick,
  onFiltersChange,
  handleSubmit: handleSubmitProp,
  children,
}) => {
  const { showMessage } = useGlobalNotification();
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={accountAppliedFilters}
      onSubmit={async (values) => {
        try {
          const isSuccess = await handleSubmitProp(values);

          if (isSuccess) {
            showMessage({
              message: t('common:filter.filterSettingsModal.successMessage'),
              color: 'success',
            });

            onFiltersChange(values);
          }
        } catch (errorGraphQL) {
          captureExceptionEvent(errorGraphQL, {
            section: 'Set account filter preferences',
          });
        } finally {
          toggleClick();
        }
      }}
    >
      {({ resetForm, handleSubmit }: FormikProps<FilterValues>): JSX.Element => (
        <Modal
          isOpen
          modalSize="small"
          title={t('common:filter.filterSettingsModal.title')}
          cancelLabel={t('common:button.cancel')}
          submitLabel={t('common:filter.filterSettingsModal.saveButton')}
          submitDisabled={loading}
          handleToggleClick={toggleClick}
          handleSubmitClick={handleSubmit}
          data-qa-id="filterSettingsModal"
          data-testid="filterSettingsModal"
        >
          <StyledNote>{t('common:filter.filterSettingsModal.note')}</StyledNote>
          <StyledClearButtonContainer>
            <StyledClearButton
              data-testid="filterSettingsModalClearButton"
              role="button"
              onClick={(): void => {
                resetForm({ values: initialFilters });
              }}
            >
              {t('common:button.clear')}
            </StyledClearButton>
          </StyledClearButtonContainer>
          <StyledForm>{children}</StyledForm>
        </Modal>
      )}
    </Formik>
  );
};

export default FilterSettingsModal;
